import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { usePermissions } from '@/hooks/usePermissions'
import { useLogtoAuth } from '@/hooks/useLogtoToken'
import { isLogtoEnabled } from '@/providers/LogtoProviderWrapper'
import { adminLogin, clearAdminSession, getAdminAccessToken, isAdminSessionExpired } from '@/services/admin-auth'
import { getCallbackBaseUrl, LOGTO_CALLBACK_PATH, POST_AUTH_REDIRECT_KEY } from '@/utils/logtoCallback'

interface AdminAuthContextValue {
  accessToken: string
  connected: boolean
  connecting: boolean
  error: string
  username: string
  password: string
  setUsername: (v: string) => void
  setPassword: (v: string) => void
  connect: () => Promise<void>
  disconnect: () => void
  isAdmin: boolean
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null)

// Logto-based admin auth provider
function LogtoAdminAuthProvider({ children }: { children: React.ReactNode }) {
  const { isAdmin, isLoading } = usePermissions()

  const value: AdminAuthContextValue = {
    accessToken: '',
    connected: isAdmin,
    connecting: isLoading,
    error: '',
    username: '',
    password: '',
    setUsername: () => {},
    setPassword: () => {},
    connect: async () => {},
    disconnect: () => {},
    isAdmin,
  }

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  )
}

// Legacy admin auth provider (for dev mode)
function LegacyAdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [username, setUsername] = useState('admin')
  const [password, setPassword] = useState('')
  const [accessToken, setAccessToken] = useState(() => getAdminAccessToken())
  const [connected, setConnected] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (isAdminSessionExpired()) {
      clearAdminSession()
      setConnected(false)
      setAccessToken('')
      return
    }
    const token = getAdminAccessToken()
    setConnected(!!token)
    setAccessToken(token)
  }, [])

  const connect = useCallback(async () => {
    setError('')
    setConnecting(true)
    try {
      const result = await adminLogin(username, password)
      setAccessToken(result.accessToken)
      setConnected(true)
    } catch (e: any) {
      setConnected(false)
      setError(e.message || 'Login failed')
    } finally {
      setConnecting(false)
    }
  }, [username, password])

  const disconnect = useCallback(() => {
    setConnected(false)
    setError('')
    clearAdminSession()
    setAccessToken('')
  }, [])

  return (
    <AdminAuthContext.Provider value={{
      accessToken, connected, connecting, error,
      username, password, setUsername, setPassword,
      connect, disconnect, isAdmin: connected
    }}>
      {children}
    </AdminAuthContext.Provider>
  )
}

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  if (isLogtoEnabled()) {
    return <LogtoAdminAuthProvider>{children}</LogtoAdminAuthProvider>
  }
  return <LegacyAdminAuthProvider>{children}</LegacyAdminAuthProvider>
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext)
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider')
  return ctx
}

// Logto admin gate component - uses context from provider to avoid duplicate state
function LogtoAdminGate({ children }: { children: React.ReactNode }) {
  // Use the shared context from AdminAuthProvider instead of calling usePermissions directly
  const { connected: isAdmin, connecting: isLoading } = useAdminAuth()
  const { isAuthenticated, isLoading: authLoading, signIn } = useLogtoAuth()
  const [showDenied, setShowDenied] = useState(false)

  // Once admin is verified, keep children mounted regardless of loading state flickers.
  // This prevents children from unmounting/remounting during token refreshes.
  const adminVerifiedRef = useRef(false)
  if (isAdmin && isAuthenticated && !authLoading && !isLoading) {
    adminVerifiedRef.current = true
  }

  useEffect(() => {
    // If not authenticated, redirect to login
    if (!authLoading && !isAuthenticated) {
      sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, '/app/admin')
      signIn(`${getCallbackBaseUrl()}${LOGTO_CALLBACK_PATH}`)
      return
    }

    // If authenticated but not admin, show denied message
    if (!authLoading && isAuthenticated && !isLoading && !isAdmin) {
      setShowDenied(true)
      // Redirect to home after 3 seconds with full page refresh to reset all state
      const timer = setTimeout(() => {
        window.location.href = '/'
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [authLoading, isAuthenticated, isLoading, isAdmin, signIn])

  // Once admin has been verified, keep children mounted to prevent state loss
  if (adminVerifiedRef.current) {
    return <>{children}</>
  }

  // Show loading while auth or permissions are loading (initial load only)
  if (authLoading || isLoading) {
    return (
      <div className="admin-login">
        <div className="admin-login-card">
          <h1>Admin Console</h1>
          <p className="admin-login-subtitle">Loading...</p>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
            <div className="loading-spinner"></div>
          </div>
        </div>
      </div>
    )
  }

  if (showDenied || (!isAdmin && isAuthenticated)) {
    return (
      <div className="admin-login">
        <div className="admin-login-card">
          <h1>Access Denied</h1>
          <p className="admin-login-subtitle" style={{ color: 'var(--color-error, #ef4444)' }}>
            You do not have admin permission to access this page.
          </p>
          <p style={{ color: 'var(--color-text-muted)', marginTop: '1rem' }}>
            Redirecting to home page...
          </p>
          <button
            onClick={() => { window.location.href = '/' }}
            style={{ marginTop: '1rem' }}
          >
            Go to Home
          </button>
        </div>
      </div>
    )
  }

  // Only render children when confirmed admin
  if (isAdmin) {
    return <>{children}</>
  }

  return null
}

// Legacy admin gate component
function LegacyAdminGate({ children }: { children: React.ReactNode }) {
  const auth = useAdminAuth()

  if (auth.connected) return <>{children}</>

  return (
    <div className="admin-login">
      <div className="admin-login-card">
        <h1>Admin Console</h1>
        <p className="admin-login-subtitle">GEO Writer Management</p>
        <div className="admin-login-form">
          <input
            type="text"
            value={auth.username}
            onChange={(e) => auth.setUsername(e.target.value)}
            placeholder="Username"
          />
          <input
            type="password"
            value={auth.password}
            onChange={(e) => auth.setPassword(e.target.value)}
            placeholder="Password"
            onKeyDown={(e) => e.key === 'Enter' && auth.connect()}
          />
          <button onClick={auth.connect} disabled={auth.connecting}>
            {auth.connecting ? 'Connecting...' : 'Sign In'}
          </button>
          {auth.error && <p className="admin-login-error">{auth.error}</p>}
        </div>
      </div>
    </div>
  )
}

export function AdminAuthGate({ children }: { children: React.ReactNode }) {
  if (isLogtoEnabled()) {
    return <LogtoAdminGate>{children}</LogtoAdminGate>
  }
  return <LegacyAdminGate>{children}</LegacyAdminGate>
}
